import { useEffect } from "react";

export interface AdjacentTarget {
  /** Puts the neighbor's data in flight — prefetchQuery no-ops when fresh. */
  prefetch: () => Promise<unknown>;
  /** Called after the data lands; return an image URL to warm, if any. */
  warmImage?: () => string | undefined;
}

/**
 * In-view first, adjacents immediately after: once the current view has
 * settled, put neighboring data (and first pixels) in flight so interaction
 * — which trails viewing — finds everything warm. Query dedupes in-flight
 * fetches and skips fresh cache, so repeats cost nothing.
 *
 * `key` is the identity of the thing being viewed. Navigating between
 * siblings keeps the component mounted with `settled` already true, so
 * without the key the effect would fire once and never again.
 */
export function useAdjacentPrefetch(
  key: string | null | undefined,
  settled: boolean,
  targets: AdjacentTarget[],
) {
  useEffect(() => {
    if (!settled) return;
    for (const { prefetch, warmImage } of targets) {
      void prefetch().then(() => {
        const src = warmImage?.();
        if (src) new Image().src = src;
      });
    }
    // Targets are recreated per render; their effective identity is the
    // (key, settled) pair — the cache layer dedupes the rest.
  }, [key, settled]);
}
